/**
 * オーディオファイルの長さ（秒）を取得する。
 *
 * WAV（RIFF / RF64）と AIFF はヘッダーのチャンクだけを読んで計算する
 * （ファイル全体を読まないので Media に大きな素材があっても軽い）。
 * それ以外の形式、またはヘッダーを解釈できない場合は decodeAudioData で
 * 全体をデコードして長さを得る。
 */

/** ファイル内 [start, end) のバイト列を返す */
export type ByteReader = (start: number, end: number) => Promise<DataView>;

const blobReader =
  (blob: Blob): ByteReader =>
  async (start, end) =>
    new DataView(await blob.slice(start, end).arrayBuffer());

const fourCC = (view: DataView, offset: number): string =>
  String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );

/** チャンク走査の上限（壊れたファイルで無限に読まないため） */
const MAX_CHUNKS = 64;

async function wavDuration(
  read: ByteReader,
  size: number,
  isRf64: boolean,
): Promise<number | null> {
  let offset = 12;
  let sampleRate = 0;
  let blockAlign = 0;
  let ds64DataSize: number | null = null;

  for (let i = 0; i < MAX_CHUNKS && offset + 8 <= size; i++) {
    const header = await read(offset, offset + 8);
    const id = fourCC(header, 0);
    const chunkSize = header.getUint32(4, true);
    const body = offset + 8;

    if (id === "ds64") {
      const ds64 = await read(body, body + 16);
      ds64DataSize =
        ds64.getUint32(8, true) + ds64.getUint32(12, true) * 2 ** 32;
    } else if (id === "fmt ") {
      const fmt = await read(body, body + 16);
      sampleRate = fmt.getUint32(4, true);
      blockAlign = fmt.getUint16(12, true);
    } else if (id === "data") {
      if (sampleRate <= 0 || blockAlign <= 0) return null;
      // 録音途中で書き出されたファイルはサイズ欄が 0 / 実体より大きいことがある
      let dataSize = isRf64 && ds64DataSize !== null ? ds64DataSize : chunkSize;
      if (dataSize === 0 || dataSize > size - body) dataSize = size - body;
      return dataSize / (sampleRate * blockAlign);
    }
    offset = body + chunkSize + (chunkSize % 2);
  }
  return null;
}

/** AIFF の 80bit 拡張精度浮動小数（サンプルレート） */
const readExtended = (view: DataView, offset: number): number => {
  const exponent = view.getUint16(offset) & 0x7fff;
  const hi = view.getUint32(offset + 2);
  const lo = view.getUint32(offset + 6);
  if (exponent === 0 && hi === 0 && lo === 0) return 0;
  return (hi * 2 ** 32 + lo) * 2 ** (exponent - 16383 - 63);
};

async function aiffDuration(
  read: ByteReader,
  size: number,
): Promise<number | null> {
  let offset = 12;
  for (let i = 0; i < MAX_CHUNKS && offset + 8 <= size; i++) {
    const header = await read(offset, offset + 8);
    const id = fourCC(header, 0);
    const chunkSize = header.getUint32(4);
    if (id === "COMM") {
      const comm = await read(offset + 8, offset + 8 + 18);
      const frames = comm.getUint32(2);
      const sampleRate = readExtended(comm, 8);
      return sampleRate > 0 ? frames / sampleRate : null;
    }
    offset += 8 + chunkSize + (chunkSize % 2);
  }
  return null;
}

/** ヘッダーから長さを読む。WAV / AIFF 以外・解釈できない場合は null */
export async function readHeaderDuration(
  read: ByteReader,
  size: number,
): Promise<number | null> {
  if (size < 12) return null;
  try {
    const head = await read(0, 12);
    const container = fourCC(head, 0);
    const format = fourCC(head, 8);
    if ((container === "RIFF" || container === "RF64") && format === "WAVE") {
      return await wavDuration(read, size, container === "RF64");
    }
    if (container === "FORM" && (format === "AIFF" || format === "AIFC")) {
      return await aiffDuration(read, size);
    }
  } catch {
    // チャンクが途中で切れている等。デコードにフォールバックする
  }
  return null;
}

async function decodedDuration(file: File): Promise<number | null> {
  if (typeof AudioContext === "undefined") return null;
  const ctx = new AudioContext();
  try {
    const buffer = await ctx.decodeAudioData(await file.arrayBuffer());
    return buffer.duration;
  } catch {
    return null;
  } finally {
    void ctx.close();
  }
}

/** ファイルの長さ（秒）。取得できない場合は null */
export async function readAudioDuration(file: File): Promise<number | null> {
  const fromHeader = await readHeaderDuration(blobReader(file), file.size);
  if (fromHeader !== null && Number.isFinite(fromHeader) && fromHeader > 0)
    return fromHeader;
  const decoded = await decodedDuration(file);
  return decoded !== null && Number.isFinite(decoded) && decoded > 0
    ? decoded
    : null;
}
